import * as Api from '@ViewModels';
import { action, computed, makeObservable, observable } from 'mobx';
import { FieldKey } from './form';
import { IMeme } from './gamification';
import { ILeadFollowUp, ISaveOutcomeRequest, LeadServedSource } from './queue';

export enum FollowUpOptionIds {
	ThisAfternoon = 'THISAFTERNOON',
	Monday = 'MONDAY',
	Tomorrow = 'TOMORROW',
	Tuesday = 'TUESDAY',
	Wednesday = 'WEDNESDAY',
	ThreeDays = 'PLUS3DAYS',
	OneWeek = 'PLUS1WEEK',
	TwoWeeks = 'PLUS2WEEKS',
	TomrrowMorning = 'TOMORROWMORNING',
	OneHour = 'ONEHOUR',
	TwoHours = 'TWOHOUR',
	ThreeHours = 'THREEHOUR',
	FiteenMinutes = '15MINUTES',
	ThirtyMinutes = '30MINUTES',
}

export enum PhoneCallStatus {
	Calling = 'calling',
	Connected = 'connected',
	Hangup = 'hangup',
	Ringing = 'ringing',
}

export interface ICallerdId {
	companyId: string;
	companyName: string;
	phoneNumber: string;
	contactId?: string;
	contactName?: string;
}

/** Mirrors the backend enum exactly — a property it doesn't declare fails deserialization server-side. */
export enum PhoneCallFilterCriteriaProperty {
	Category = 'Category',
	Company = 'Company',
	Date = 'Date',
	Direction = 'Direction',
	HasTranscription = 'HasTranscription',
	User = 'User',
}

export type IPhoneCallFilterCriteria = Api.IFilterCriteria<PhoneCallFilterCriteriaProperty>;

export interface IPhoneCallItem {
	contacts?: Api.IUser[];
	company?: Api.ICompany;
	phoneCall: Api.IPhoneCall;
}

export enum OutcomeType {
	OutcomeGroup = 'OutcomeGroup',
	PhoneCallOutcome = 'PhoneCallOutcome',
}

export interface IOutcome {
	description?: string;
	hexColor?: string;
	id?: string;
	isShortCut?: boolean;
	/** Is the value to display in the UI !important, this value can change at any time */
	label: string;
	/** Is the value to key off of to identify the outcome specifically !important, this value should never change */
	name: FieldKey;
	points?: number;
	_type: OutcomeType;
}

export interface IPhoneCallOutcome extends IOutcome {
	requireNote?: boolean;
}

export interface IPhoneCallOutcomeGroup extends IOutcome {
	children: IPhoneCallOutcome[];
	hexColor: string;
}

export interface IPhoneCallRemoteResourceEvent extends Api.IBaseApiModel {
	calling?: string;
	connected?: string;
	hangup?: string;
	ringing?: string;
	provider?: string;
	clientType?: string;
}

export interface IRealtimeTranscriptionUtteranceEvent {
	transcription: string;
	speaker: string;
	final: boolean;
	sequenceId: string;
	stability: string;
	phoneCallId: string;
	lastSentence: boolean;
}

export class PhoneCallViewModel extends Api.ViewModel {
	private mPhoneCall: Api.IPhoneCall;
	private mOutcome: IPhoneCallOutcome = null;
	public status: PhoneCallStatus;
	public isSaving = false;
	private mCallingTimeout: number;

	constructor(userSession: Api.UserSessionContext, phoneCall: Api.IPhoneCall) {
		super(userSession);

		makeObservable<PhoneCallViewModel, 'mPhoneCall' | 'mOutcome'>(this, {
			mPhoneCall: observable,
			mOutcome: observable,
			status: observable,
			isSaving: observable,
			id: computed,
			outcome: computed,
			phoneNumber: computed,
			fromPhoneNumber: computed,
			getCallMetadataDisplayString: computed,
			saveOutcome: action,
			setOutcome: action,
			update: action,
			updateStatus: action,
		});

		this.mPhoneCall = phoneCall;
	}

	public get id() {
		return this.mPhoneCall.id;
	}

	public get outcome() {
		return this.mOutcome;
	}

	public get phoneNumber() {
		return this.mPhoneCall.phoneNumber;
	}

	public get fromPhoneNumber() {
		return this.mPhoneCall.creatorPhoneNumber;
	}

	public get getCallMetadataDisplayString() {
		if (this.id === 'manual') {
			return `Manual Dial`;
		}

		if (!this.phoneNumber || !this.fromPhoneNumber) {
			return null;
		}

		return `Calling ${this.phoneNumber.standard} from ${this.fromPhoneNumber.standard}`;
	}

	public toJs() {
		return this.mPhoneCall;
	}

	public saveOutcome = async (
		noteId: string,
		followUpDate: FollowUpOptionIds | ILeadFollowUp,
		doNotContact: boolean,
		source: LeadServedSource,
		additionalOptions?: { decisionMaker: string }
	) => {
		if (!this.isSaving) {
			this.isSaving = true;
			const body: ISaveOutcomeRequest = {
				customProperties: additionalOptions,
				doNotContact,
				leadServedSource: source,
				noteId,
				outcomeId: this.outcome.id,
			};

			if (Object.values(FollowUpOptionIds).includes(followUpDate as FollowUpOptionIds)) {
				body.followUpOptionId = followUpDate as FollowUpOptionIds;
			} else if (followUpDate === null) {
				body.followUpOptionId = null;
			} else if ('scheduled' in (followUpDate as ILeadFollowUp)) {
				const followUpSpecific = followUpDate as ILeadFollowUp;
				body.followUpDate = followUpSpecific.followUpDate;
				body.followUpImportanceLevel = followUpSpecific.importance;
				body.followUpName = followUpSpecific.name;
				body.scheduledFollowUp = followUpSpecific.scheduled;
			}

			const url =
				this.id === 'manual'
					? this.composeApiUrl({ urlPath: `lead/${this.mPhoneCall.companyId}/outcome` })
					: this.composeApiUrl({ urlPath: `phonecall/${this.id}/outcome` });

			const result = await this.userSession.webServiceHelper.callWebServiceAsync<{
				meme: IMeme;
			}>(url, 'POST', body);

			if (!result.success) {
				this.isSaving = false;
				throw Api.asApiError(result);
			}

			this.isSaving = false;
			return result.value?.meme;
		}
	};

	public setOutcome = (outcome: IPhoneCallOutcome) => {
		this.mOutcome = outcome;
	};

	public update = (phoneCall: Api.IPhoneCall) => {
		this.mPhoneCall = {
			...this.mPhoneCall,
			...phoneCall,
		};
	};

	public updateStatus = (event: IPhoneCallRemoteResourceEvent) => {
		const { calling, ringing, connected, hangup } = event;

		// order matters here!
		// event can have some or all of the properties
		// in IPhoneCallRemoteResourceEvent at any given
		// time, so this order should not be modified
		if (hangup) {
			this.status = PhoneCallStatus.Hangup;
			window.clearTimeout(this.mCallingTimeout);
		} else if (connected) {
			this.status = PhoneCallStatus.Connected;
			window.clearTimeout(this.mCallingTimeout);
		} else if (calling) {
			this.status = PhoneCallStatus.Calling;
			// if a call is never connected, we will not receive
			// a hangup event from dialpad. this can cause the ui
			// to be put into an infinite calling state. to prevent
			// we are adding a timeout so if a calling state does
			// not change in 30 seconds, we will automatically mark
			// the call as disconnected
			this.mCallingTimeout = window.setTimeout(() => {
				this.status = PhoneCallStatus.Hangup;
			}, 1000 * 20);
		} else if (ringing) {
			this.status = PhoneCallStatus.Ringing;
		}
	};
}

/** Keep track of the users phone calls they have made (per lead) When going to a new lead, the view model is reset */
export class PhoneCallsViewModel extends Api.ViewModel {
	private mCurrentCall: PhoneCallViewModel = null;
	private mIncomingCalls: PhoneCallViewModel[] = [];

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<PhoneCallsViewModel, 'mCurrentCall' | 'mIncomingCalls'>(this, {
			mCurrentCall: observable,
			mIncomingCalls: observable,
			incomingCalls: computed,
			currentCall: computed,
			setExistingCall: action,
			clearIncomingCalls: action,
			clearCurrentCall: action,
			softReset: action,
			reset: action,
		});
	}

	get incomingCalls() {
		return this.mIncomingCalls;
	}

	set incomingCalls(value: PhoneCallViewModel[]) {
		this.mIncomingCalls = value;
	}

	/*
	 *The call that the user has made, stored until the user saves the outcome/disposition of the call
	 */
	get currentCall() {
		return this.mCurrentCall;
	}

	set currentCall(value: PhoneCallViewModel) {
		this.mCurrentCall = value;
	}

	/** Rehydrate a call that has been made, but the page has been refreshed. It must be reloaded from the server */
	public setExistingCall = (phoneCall: Api.IPhoneCall, companyId: string) => {
		this.mCurrentCall = new PhoneCallViewModel(this.userSession, {
			...phoneCall,
			companyId,
		});
	};

	public clearIncomingCalls = () => {
		this.mIncomingCalls = [];
	};

	public clearCurrentCall = () => {
		this.mCurrentCall = null;
	};

	public softReset = () => {
		this.mCurrentCall = null;
		this.mIncomingCalls = [];
	};

	public reset = () => {
		this.softReset();
	};
}
